import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOptions,
  ComboboxOption,
  Transition,
} from '@headlessui/react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { Fragment, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

export type MultiSelectOption = {
  id: string | number;
  label: string;
  value: string;
};

interface MultiSelectProps {
  label?: string;
  error?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  name: string;
  options: MultiSelectOption[];
  value?: MultiSelectOption[];
  onChange?: (value: MultiSelectOption[]) => void;
  placeholder?: string;
  showTags?: boolean;
}

export function MultiSelect({
  label,
  error: propError,
  className,
  required,
  disabled,
  name,
  options,
  value: propValue,
  onChange: propOnChange,
  placeholder = 'Select options...',
  showTags = false,
}: MultiSelectProps): JSX.Element {
  const form = useFormContext();
  const error = propError || (form?.formState?.errors[name]?.message as string);
  const [query, setQuery] = useState('');
  const [maxVisibleTags, setMaxVisibleTags] = useState(3);

  // Initialize internal value state if not using form context
  const [internalValue, setInternalValue] = useState<MultiSelectOption[]>([]);

  // Set responsive max visible tags based on screen size
  useEffect(() => {
    const updateMaxVisibleTags = (): void => {
      if (window.innerWidth < 640) {
        setMaxVisibleTags(1); // Mobile: 1 tag
      } else if (window.innerWidth < 768) {
        setMaxVisibleTags(2); // Small tablet: 2 tags
      } else if (window.innerWidth < 1024) {
        setMaxVisibleTags(3); // Tablet: 3 tags
      } else {
        setMaxVisibleTags(4); // Desktop: 4 tags
      }
    };

    updateMaxVisibleTags();
    window.addEventListener('resize', updateMaxVisibleTags);
    return () => window.removeEventListener('resize', updateMaxVisibleTags);
  }, []);

  // Get value from form context or props or internal state
  const value = form
    ? (form.watch(name) as MultiSelectOption[])
    : propValue ?? internalValue;

  const filteredOptions = options
    .filter((option) =>
      option.label.toLowerCase().includes(query.toLowerCase()),
    )
    .filter((option) => {
      if (!showTags) return true;
      // Filter out already selected options when showTags is enabled
      return !value?.some((selected) => selected.id === option.id);
    });

  const handleChange = (newValue: MultiSelectOption[]): void => {
    if (form) {
      form.setValue(name, newValue, { shouldValidate: true });
    } else {
      setInternalValue(newValue);
    }
    propOnChange?.(newValue);
  };

  const removeOption = (optionToRemove: MultiSelectOption): void => {
    const newValue = value.filter((v) => v.id !== optionToRemove.id);
    handleChange(newValue);
  };

  return (
    <div className={clsx('w-full space-y-1', className)}>
      <Combobox
        value={value || []}
        onChange={handleChange}
        multiple
        disabled={disabled}
      >
        {({ open }) => (
          <>
            {label && (
              <ComboboxLabel className="flex-1 font-nunito text-sm font-normal leading-6 text-[#434343]">
                {label}
                {required && <span className="text-[#CE3F4F] ml-1">*</span>}
              </ComboboxLabel>
            )}
            <div className="relative mt-1">
              <div
                className={clsx(
                  'relative w-full cursor-default overflow-hidden rounded-[6px] transition-all duration-300 h-10',
                  disabled
                    ? 'bg-[var(--Color-Neutral-Fill-colorFillTertiary,rgba(0,0,0,0.04))] border-[1px] border-[var(--Border-border-gray,#D9D9D9)]'
                    : 'border border-[#D9D9D9] bg-[#FEFDFC]',
                  error && 'border-red-300',
                )}
              >
                {showTags ? (
                  <div className="flex items-center gap-1 p-1 min-h-[38px]">
                    <div className="flex items-center gap-1 flex-shrink-0 max-w-[60%] overflow-hidden flex-nowrap">
                      {value?.slice(0, maxVisibleTags).map((item) => (
                        <span
                          key={item.id}
                          className={clsx(
                            'inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm transition-colors duration-300 flex-shrink-0 max-w-[120px]',
                            disabled
                              ? 'bg-[var(--Color-Neutral-Fill-colorFillTertiary,rgba(0,0,0,0.04))] text-[var(--Text-text-disabled,#8C8C8C)]'
                              : 'bg-gray-100 text-gray-900',
                          )}
                        >
                          <span className="truncate" title={item.label}>
                            {item.label}
                          </span>
                          {!disabled && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                removeOption(item);
                              }}
                              className="rounded-sm hover:bg-gray-200 flex-shrink-0"
                            >
                              <XMarkIcon className="h-4 w-4 text-gray-900" />
                            </button>
                          )}
                        </span>
                      ))}
                      {value && value.length > maxVisibleTags && (
                        <span
                          className={clsx(
                            'inline-flex items-center rounded-md px-2 py-1 text-sm flex-shrink-0',
                            disabled
                              ? 'bg-[var(--Color-Neutral-Fill-colorFillTertiary,rgba(0,0,0,0.04))] text-[var(--Text-text-disabled,#8C8C8C)]'
                              : 'bg-gray-100 text-gray-900',
                          )}
                        >
                          +{value.length - maxVisibleTags} more
                        </span>
                      )}
                    </div>
                    <ComboboxButton className="flex-1 min-w-[120px] h-full">
                      <ComboboxInput
                        className={clsx(
                          'w-full border-none py-1 pl-3 pr-10 h-full',
                          'font-roboto text-base leading-6',
                          disabled ? 'text-[#8C8C8C]' : 'text-[#141414]',
                          'focus:outline-none',
                        )}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={value?.length === 0 ? placeholder : ''}
                      />
                    </ComboboxButton>
                  </div>
                ) : (
                  <ComboboxButton className=" inset-y-0 right-0 flex w-full h-full items-center pr-2">
                    <ComboboxInput
                      className={clsx(
                        'w-full border-none py-2.5 pl-3 pr-10',
                        'font-roboto text-base leading-6',
                        disabled ? 'text-[#8C8C8C]' : 'text-[#141414]',
                        'focus:outline-none',
                        'overflow-hidden whitespace-nowrap text-ellipsis',
                      )}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder={placeholder}
                    />
                    <ChevronDownIcon
                      className={clsx(
                        'h-5 w-5 transition-transform duration-200',
                        disabled ? 'text-[#8C8C8C]' : 'text-[#141414]',
                        open && 'rotate-180',
                      )}
                      aria-hidden="true"
                    />
                  </ComboboxButton>
                )}
                <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDownIcon
                    className={clsx(
                      'h-5 w-5 transition-transform duration-200',
                      disabled ? 'text-[#8C8C8C]' : 'text-gray-900',
                      open && 'rotate-180',
                    )}
                    aria-hidden="true"
                  />
                </ComboboxButton>
              </div>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => setQuery('')}
              >
                <ComboboxOptions
                  className={clsx(
                    'absolute z-10 mt-1 max-h-60 w-full overflow-auto',
                    'rounded-md bg-white py-1 shadow-lg',
                    'ring-1 ring-black ring-opacity-5',
                    'custom-scrollbar',
                  )}
                >
                  {filteredOptions.length === 0 && query !== '' ? (
                    <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                      Nothing found.
                    </div>
                  ) : (
                    filteredOptions.map((option) => (
                      <ComboboxOption
                        key={option.id}
                        value={option}
                        className={clsx(
                          'relative cursor-pointer select-none py-2 pl-10 pr-4',
                          'text-gray-900',
                        )}
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={clsx(
                                'block truncate',
                                selected ? 'font-medium' : 'font-normal',
                              )}
                            >
                              {option.label}
                            </span>
                            {selected && (
                              <span
                                className={clsx(
                                  'absolute inset-y-0 left-0 flex items-center pl-3',
                                  'text-blue-600',
                                )}
                              >
                                ✓
                              </span>
                            )}
                          </>
                        )}
                      </ComboboxOption>
                    ))
                  )}
                </ComboboxOptions>
              </Transition>
            </div>
          </>
        )}
      </Combobox>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
